import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Upload, Link2, ImageOff, Eye, PenLine, Save, Send } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage, mediaUrl } from "../../api";

const EMPTY = { title: "", slug: "", excerpt: "", category: "", author: "Fleetroot Team", date: new Date().toISOString().slice(0, 10), cover: "", content: "", status: "draft", seoTitle: "", seoDescription: "", ogImage: "" };
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);

const Field = ({ label, hint, children }) => (
  <label className="block text-sm font-medium">
    <span className="flex items-baseline justify-between">
      {label} {hint && <span className="text-xs font-normal text-[var(--fr-muted)]">{hint}</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default function AdminPostEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [coverMode, setCoverMode] = useState("upload");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/admin/blog").then((r) => setCategories(Array.from(new Set(r.data.map((p) => p.category))).sort())).catch(() => {});
    if (isNew) return;
    api
      .get(`/admin/blog/${id}`)
      .then((r) => setForm({ ...EMPTY, ...r.data }))
      .catch((e) => {
        toast.error(errorMessage(e));
        nav("/admin/blog");
      })
      .finally(() => setLoading(false));
  }, [id, isNew, nav]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v, ...(k === "title" && !slugTouched ? { slug: slugify(v) } : {}) }));
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover: data.url }));
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async (status) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, status, slug: form.slug || undefined, category: form.category || "Insights" };
    try {
      const { data } = isNew ? await api.post("/admin/blog", payload) : await api.put(`/admin/blog/${id}`, payload);
      toast.success(status === "published" ? "Post published" : "Draft saved");
      if (isNew) nav(`/admin/blog/${data.id}`, { replace: true });
      else setForm({ ...EMPTY, ...data });
    } catch (e) {
      toast.error(errorMessage(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--fr-muted)]">Loading…</div>;

  const words = form.content.trim() ? form.content.trim().split(/\s+/).length : 0;

  return (
    <div data-testid="admin-post-editor">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="min-w-0">
          <Link to="/admin/blog" className="inline-flex items-center gap-1.5 text-sm text-[var(--fr-muted)] hover:text-[var(--fr-ink)]" data-testid="editor-back">
            <ArrowLeft size={14} /> All posts
          </Link>
          <h1 className="font-display font-bold text-2xl md:text-3xl mt-2 truncate">{isNew ? "New post" : form.title || "Edit post"}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isNew && (
            <span className={`chip !text-[11px] uppercase tracking-wide ${form.status === "published" ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"}`} data-testid="editor-status">
              {form.status}
            </span>
          )}
          <button type="button" onClick={() => save("draft")} disabled={saving} className="btn btn-outline btn-sm disabled:opacity-60" data-testid="editor-save-draft">
            <Save size={14} className="mr-1.5" /> Save draft
          </button>
          <button type="button" onClick={() => save("published")} disabled={saving} className="btn btn-primary btn-sm disabled:opacity-60" data-testid="editor-publish">
            <Send size={14} className="mr-1.5" /> {form.status === "published" && !isNew ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-5 min-w-0">
          <div className="card p-5 md:p-6 space-y-5">
            <Field label="Title">
              <input className="fr-input !text-lg !h-12 font-display font-bold" placeholder="A headline people want to click" value={form.title} onChange={set("title")} data-testid="editor-title" />
            </Field>
            <Field label="Excerpt" hint="Shown on cards · optional, auto-generated if empty">
              <textarea className="fr-input" rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="One or two sentences summarising the article." data-testid="editor-excerpt" />
            </Field>
          </div>

          <div className="card overflow-hidden">
            <div className="flex items-center justify-between px-5 h-12 border-b border-[var(--fr-border)] bg-[var(--fr-bg)]">
              <div className="flex gap-1" data-testid="editor-mode-tabs">
                <button type="button" onClick={() => setPreview(false)} className={`h-8 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${!preview ? "bg-white shadow-sm text-[var(--fr-ink)]" : "text-[var(--fr-muted)]"}`} data-testid="editor-tab-write">
                  <PenLine size={14} /> Write
                </button>
                <button type="button" onClick={() => setPreview(true)} className={`h-8 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${preview ? "bg-white shadow-sm text-[var(--fr-ink)]" : "text-[var(--fr-muted)]"}`} data-testid="editor-tab-preview">
                  <Eye size={14} /> Preview
                </button>
              </div>
              <span className="text-xs text-[var(--fr-muted)]">
                Markdown · {words} words · ~{Math.max(1, Math.ceil(words / 220))} min
              </span>
            </div>
            {preview ? (
              <div className="prose-fr p-6 md:p-8 min-h-[420px]" data-testid="editor-preview">
                {form.content.trim() ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content}</ReactMarkdown> : <p className="text-[var(--fr-muted)]">Nothing to preview yet.</p>}
              </div>
            ) : (
              <textarea
                className="w-full min-h-[420px] p-6 md:p-8 font-mono text-[14px] leading-relaxed outline-none resize-y bg-white"
                placeholder={"## Start with a heading\n\nWrite in Markdown. **Bold**, _italic_, lists, links and tables all work."}
                value={form.content}
                onChange={set("content")}
                data-testid="editor-content"
              />
            )}
          </div>
        </div>

        <aside className="space-y-5">
          <div className="card p-5 space-y-4">
            <h3 className="font-display font-bold">Cover image</h3>
            <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[var(--fr-bg-alt)] flex items-center justify-center text-[var(--fr-muted)]" data-testid="editor-cover-preview">
              {form.cover ? <img src={mediaUrl(form.cover)} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : <ImageOff size={22} />}
            </div>
            <div className="flex gap-1 bg-[var(--fr-bg-alt)] p-1 rounded-xl">
              {[
                ["upload", Upload, "Upload"],
                ["url", Link2, "Paste URL"],
              ].map(([m, Icon, label]) => (
                <button key={m} type="button" onClick={() => setCoverMode(m)} className={`flex-1 h-8 rounded-lg text-xs font-medium inline-flex items-center justify-center gap-1.5 ${coverMode === m ? "bg-white shadow-sm" : "text-[var(--fr-muted)]"}`} data-testid={`editor-cover-mode-${m}`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            {coverMode === "upload" ? (
              <>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="editor-cover-file" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-outline btn-sm w-full disabled:opacity-60" data-testid="editor-cover-upload">
                  <Upload size={14} className="mr-1.5" /> {uploading ? "Uploading…" : form.cover ? "Replace image" : "Choose image"}
                </button>
                <p className="text-xs text-[var(--fr-muted)]">JPG, PNG or WEBP · up to 8 MB · 16:10 works best</p>
              </>
            ) : (
              <input className="fr-input !h-10 text-sm" placeholder="https://…/image.jpg" value={form.cover} onChange={set("cover")} data-testid="editor-cover-url" />
            )}
          </div>

          <div className="card p-5 space-y-4">
            <h3 className="font-display font-bold">Details</h3>
            <Field label="Category">
              <input className="fr-input !h-10 text-sm" list="category-options" placeholder="e.g. Industry Trends" value={form.category} onChange={set("category")} data-testid="editor-category" />
              <datalist id="category-options">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>
            <Field label="Author">
              <input className="fr-input !h-10 text-sm" value={form.author} onChange={set("author")} data-testid="editor-author" />
            </Field>
            <Field label="Publish date">
              <input className="fr-input !h-10 text-sm" type="date" value={form.date} onChange={set("date")} data-testid="editor-date" />
            </Field>
            <Field label="URL slug" hint="/blog/…">
              <input
                className="fr-input !h-10 text-sm font-mono"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set("slug")(slugify(e.target.value));
                }}
                data-testid="editor-slug"
              />
            </Field>
          </div>

          <div className="card p-5 space-y-4" data-testid="editor-seo-card">
            <h3 className="font-display font-bold">SEO <span className="text-xs font-normal text-[var(--fr-muted)] ml-2">optional</span></h3>
            <Field label="Meta title" hint={`${(form.seoTitle || "").length}/70`}>
              <input className="fr-input !h-10 text-sm" maxLength={70} placeholder="Defaults to the title" value={form.seoTitle || ""} onChange={set("seoTitle")} data-testid="editor-seo-title" />
            </Field>
            <Field label="Meta description" hint={`${(form.seoDescription || "").length}/160`}>
              <textarea className="fr-input text-sm" rows={3} maxLength={160} placeholder="Defaults to the excerpt / summary" value={form.seoDescription || ""} onChange={set("seoDescription")} data-testid="editor-seo-description" />
            </Field>
            <Field label="Social share image URL" hint="defaults to cover">
              <input className="fr-input !h-10 text-sm" placeholder="https://…/image.jpg" value={form.ogImage || ""} onChange={set("ogImage")} data-testid="editor-seo-og-image" />
            </Field>
          </div>
        </aside>
      </div>
    </div>
  );
}
